/**
 * Category settings — Overview and Pipeline (parity: curator's category
 * detail tabs). These are the controls that make generation produce something
 * a customer actually wants: the briefing that frames every prompt, and the
 * per-stage writer / sourcing / prompt text.
 *
 * The engine reads all of it (worker `contextFor` → core `prompts.ts`). What
 * gets composed around this text — briefing, sourcing directive, material
 * block, accuracy floor — is shown by the wizard's prompt preview rather than
 * described in a banner here.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '@servicecompressionllc/sc-design-system';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import {
  knowledgeApi,
  type KnowledgeStage,
  type KnowledgeTemplate,
  type UpdateTemplateBody,
} from './api.js';
import { Field, StageEditor, TextArea } from './stageFields.js';

export const CategoryOverviewTab = ({ template }: { template: KnowledgeTemplate }) => {
  const me = useMe();
  const readOnly = me.data ? !canWriteKnowledge(me.data.user) : true;
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: template.name,
    description: template.description ?? '',
    subjectNoun: template.subjectNoun,
    itemNoun: template.itemNoun,
    promptContext: template.promptContext,
  });
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      knowledgeApi.updateTemplate(template.id, {
        name: form.name.trim(),
        description: form.description.trim().length > 0 ? form.description : null,
        subjectNoun: form.subjectNoun.trim(),
        itemNoun: form.itemNoun.trim(),
        promptContext: form.promptContext,
      }),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['knowledge', 'templates'] });
    },
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm({ ...form, [k]: v });
    setSaved(false);
  };

  return (
    <div className="max-w-3xl rounded-lg border border-outline bg-surface-elevated p-4">
      <Field label="Category name">
        <Input
          value={form.name}
          aria-label="Category name"
          onChange={(e) => set('name', e.target.value)}
          className="mt-1 w-full"
        />
      </Field>
      <Field label="Description">
        <Input
          value={form.description}
          aria-label="Category description"
          onChange={(e) => set('description', e.target.value)}
          className="mt-1 w-full"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="What you call one of these" hint="“Component”, “Unit”, “Panel”.">
          <Input
            value={form.subjectNoun}
            aria-label="Subject noun"
            onChange={(e) => set('subjectNoun', e.target.value)}
            className="mt-1 w-full"
          />
        </Field>
        <Field label="What you call one of its guides" hint="“Failure mode”, “Procedure”.">
          <Input
            value={form.itemNoun}
            aria-label="Item noun"
            onChange={(e) => set('itemNoun', e.target.value)}
            className="mt-1 w-full"
          />
        </Field>
      </div>
      <Field
        label="Category briefing"
        hint="What the AI should know before writing anything here — vocabulary, what this equipment is and isn't, constraints. This goes into every prompt for every subject in this category."
      >
        <TextArea
          value={form.promptContext}
          onChange={(v) => set('promptContext', v)}
          label="Category briefing"
          rows={8}
          disabled={readOnly}
        />
      </Field>
      <div className="flex items-center gap-3">
        {!readOnly && (
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        )}
        {saved && !save.isPending && <span className="text-sm text-success">Saved.</span>}
        {save.error && <span className="text-sm text-danger">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
};

export const CategoryPipelineTab = ({ template }: { template: KnowledgeTemplate }) => {
  const me = useMe();
  // Curator shows this to everyone and disables the inputs for non-admins —
  // deliberate transparency ("this is what the AI was told"), not a leak.
  const readOnly = me.data ? !canWriteKnowledge(me.data.user) : true;
  const qc = useQueryClient();
  const [list, setList] = useState<KnowledgeStage>(template.list);
  const [item, setItem] = useState<KnowledgeStage>(template.item);
  const [dirty, setDirty] = useState<{ list: boolean; item: boolean }>({
    list: false,
    item: false,
  });
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      // Send only the stage that was touched. Sending both would revert a
      // colleague's concurrent edit to the other stage using our mount-time
      // copy of it.
      const body: UpdateTemplateBody = {
        ...(dirty.list ? { list } : {}),
        ...(dirty.item ? { item } : {}),
      };
      return knowledgeApi.updateTemplate(template.id, body);
    },
    onSuccess: () => {
      setSaved(true);
      setDirty({ list: false, item: false });
      void qc.invalidateQueries({ queryKey: ['knowledge', 'templates'] });
    },
  });

  return (
    <div className="flex max-w-3xl flex-col gap-3">
      <StageEditor
        title={`Step 1 — propose the list of ${template.itemNoun.toLowerCase()}s`}
        blurb="Produces a list for your team to review. Nothing is written until you keep the entries you want."
        stage={list}
        stageName="Step 1"
        stageKind="list"
        readOnly={readOnly}
        onChange={(s) => {
          setList(s);
          setDirty((d) => ({ ...d, list: true }));
          setSaved(false);
        }}
      />
      <StageEditor
        title={`Step 2 — write each ${template.itemNoun.toLowerCase()}`}
        blurb="Writes one guide per entry you kept. Each arrives as a draft for review."
        stage={item}
        stageName="Step 2"
        stageKind="item"
        readOnly={readOnly}
        onChange={(s) => {
          setItem(s);
          setDirty((d) => ({ ...d, item: true }));
          setSaved(false);
        }}
      />

      <div className="flex items-center gap-3">
        {!readOnly && (
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || (!dirty.list && !dirty.item)}
          >
            {save.isPending ? 'Saving…' : 'Save pipeline'}
          </Button>
        )}
        {saved && !save.isPending && <span className="text-sm text-success">Saved.</span>}
        {save.error && <span className="text-sm text-danger">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
};
